import { useCallback, useRef, RefObject } from "react";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { UploadSource } from "@/components/upload/types/upload-item";
import { FolderMetadataMap, FileMetadata } from "@/types/folder-metadata";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { Item } from "../utils/workspaceItemUtils";
import { useUploadStateStore } from "@/stores/upload-state.store";
import { useUploadMetadataStore } from "@/stores/upload-metadata.store";
import { DownloadedFileWithIds } from "../providers/SharePointProvider";
import Workspace from "@/models/workspace/Workspace";
import {
  checkForFileNameConflicts,
  getWorkspaceFileNames,
} from "../services/conflictDetectionService";
import { FileWithRelativePath } from "../types/upload.types";
import { logger } from "@/lib/logger";

type UseUploadDialogFlowParams = {
  workspaceId: string | null;
  currentWorkspaceIdRef: React.MutableRefObject<string | null>;
  fileInputRef: RefObject<HTMLInputElement>;
  // Upload functions
  onUploadFiles: (
    e: React.ChangeEvent<HTMLInputElement>,
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  handleUploadFiles: (
    filesWithIds: DownloadedFileWithIds[],
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  handleFolderUpload: (
    files: FileList,
    workspace: Workspace,
    folderId?: string,
    providedMetadata?: Record<string, WorkspaceDocumentMetadata[]>,
    skipConflictCheck?: boolean,
    skipMetadataCheck?: boolean,
    fileMetadataMap?: Map<
      string,
      {
        source: UploadSource;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
      }
    >,
    folderMetadataMap?: FolderMetadataMap
  ) => Promise<void>;
  // Data fetchers
  ensureTenantMetadata: () => Promise<{ id?: string }[]>;
  ensureWorkspaceData: () => Promise<Workspace | null>;
  workspaceData: Workspace | undefined;
};

type UseUploadDialogFlowReturn = {
  onFileInputChange: (
    files: FileList,
    currentItems: Item[],
    folderId?: string
  ) => Promise<void>;
  onFolderInputChange: (
    files: FileList,
    workspace: Workspace,
    folderId?: string
  ) => void;
  onOverwriteFilesCancel: () => void;
  onOverwriteFilesConfirm: () => Promise<void>;
  onMetadataCancel: () => void;
  onMetadataConfirm: (
    cleanedMetadata: WorkspaceDocumentMetadata[][]
  ) => Promise<void>;
  uploadProcessedRef: React.MutableRefObject<boolean>;
};

/**
 * Hook to handle dialog flow for file uploads (conflicts, metadata)
 */
export const useUploadDialogFlow = ({
  workspaceId,
  currentWorkspaceIdRef,
  fileInputRef,
  onUploadFiles,
  handleUploadFiles,
  handleFolderUpload,
  ensureTenantMetadata,
  ensureWorkspaceData,
  workspaceData,
}: UseUploadDialogFlowParams): UseUploadDialogFlowReturn => {
  const uploadProcessedRef = useRef(false);

  const {
    isUploadInProgress,
    setConflictingFiles,
    setIsConflictDialogOpen,
    setPendingUploadFiles,
    setPendingWorkspaceFileNames,
    setPendingFolderId,
    setIsUploadMetadataDialogOpen,
    setPendingMetadataFiles,
    setPendingMetadataWorkspaceFileNames,
    setPendingMetadataFolderId,
    setPendingFolderMetadataMap,
    setIsUploadInProgress,
    clearConflictState,
    clearPendingUploadState,
    clearPendingOneDriveState,
    clearPendingMetadataState,
    clearAll: clearUploadState,
  } = useUploadStateStore();

  const { clearAll: clearUploadMetadataStore } = useUploadMetadataStore();

  const onFileInputChange = useCallback(
    async (files: FileList, currentItems: Item[], folderId?: string) => {
      const filesArray = Array.from(files);
      const workspaceFileNames = getWorkspaceFileNames(
        currentItems,
        workspaceData,
        folderId
      );

      if (filesArray.length === 0) {
        return;
      }

      const dataTransfer = new DataTransfer();
      filesArray.forEach((file) => dataTransfer.items.add(file));
      const filteredFiles = dataTransfer.files;

      const conflicts = checkForFileNameConflicts(
        filteredFiles,
        currentItems,
        workspaceData,
        folderId
      );

      if (conflicts.length > 0) {
        setConflictingFiles(conflicts);
        setPendingUploadFiles(filteredFiles);
        setPendingWorkspaceFileNames(workspaceFileNames);
        setPendingFolderId(folderId);
        setIsConflictDialogOpen(true);
      } else {
        const metadataList = await ensureTenantMetadata();

        if (metadataList && metadataList.length > 0) {
          setPendingUploadFiles(filteredFiles);
          setPendingWorkspaceFileNames(workspaceFileNames);
          setPendingFolderId(folderId);
          setPendingMetadataFiles(filteredFiles);
          setPendingMetadataWorkspaceFileNames(workspaceFileNames);
          setPendingMetadataFolderId(folderId);
          setIsUploadMetadataDialogOpen(true);
        } else {
          onUploadFiles(
            {
              target: { files: filteredFiles },
            } as React.ChangeEvent<HTMLInputElement>,
            workspaceFileNames,
            folderId
          );
        }
      }
    },
    [
      workspaceData,
      ensureTenantMetadata,
      setConflictingFiles,
      setPendingUploadFiles,
      setPendingWorkspaceFileNames,
      setPendingFolderId,
      setIsConflictDialogOpen,
      setIsUploadMetadataDialogOpen,
      setPendingMetadataFiles,
      setPendingMetadataWorkspaceFileNames,
      setPendingMetadataFolderId,
      onUploadFiles,
    ]
  );

  const onFolderInputChange = useCallback(
    (files: FileList, workspace: Workspace, folderId?: string) => {
      handleFolderUpload(
        files,
        workspace,
        folderId,
        undefined,
        false,
        false,
        undefined
      );
    },
    [handleFolderUpload]
  );

  const onOverwriteFilesCancel = useCallback(() => {
    clearUploadState();
    clearUploadMetadataStore();

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [clearUploadState, clearUploadMetadataStore, fileInputRef]);

  const onOverwriteFilesConfirm = useCallback(async () => {
    const {
      pendingUploadFiles,
      pendingWorkspaceFileNames,
      pendingFolderId,
      pendingOneDriveFiles,
    } = useUploadStateStore.getState();

    if (pendingUploadFiles) {
      const hasFolderStructure = Array.from(pendingUploadFiles).some(
        (file) => (file as FileWithRelativePath).webkitRelativePath
      );

      if (hasFolderStructure) {
        const metadataList = await ensureTenantMetadata();
        if (metadataList && metadataList.length > 0) {
          setPendingMetadataFiles(pendingUploadFiles);
          setPendingMetadataWorkspaceFileNames(pendingWorkspaceFileNames);
          setPendingMetadataFolderId(pendingFolderId);
          setIsUploadMetadataDialogOpen(true);
          clearConflictState();
        } else {
          const workspace = await ensureWorkspaceData();
          if (!workspace) {
            logger.error("Upload aborted: workspace data unavailable", {
              operation: "confirmOverwriteFiles",
              folderId: pendingFolderId,
            });
            showErrorToast("Workspace data mangler. Prøv at opdatere siden.");
            clearPendingUploadState();
            clearConflictState();
            return;
          }

          handleFolderUpload(
            pendingUploadFiles,
            workspace,
            pendingFolderId,
            undefined,
            true,
            true,
            undefined
          )
            .then(() => {
              clearPendingUploadState();
              clearConflictState();
            })
            .catch((error) => {
              logger.error(
                "Failed to upload folder after overwrite confirmation",
                {
                  operation: "confirmOverwriteFiles",
                  error,
                  workspaceId: workspace.id,
                  folderId: pendingFolderId,
                }
              );
              showErrorToast("Fejl ved upload af mappe");
              clearPendingUploadState();
              clearConflictState();
            });
        }
        return;
      }

      const metadataList = await ensureTenantMetadata();
      if (metadataList && metadataList.length > 0) {
        setPendingMetadataFiles(pendingUploadFiles);
        setPendingMetadataWorkspaceFileNames(pendingWorkspaceFileNames);
        setPendingMetadataFolderId(pendingFolderId);
        setIsUploadMetadataDialogOpen(true);
        clearConflictState();
      } else {
        onUploadFiles(
          {
            target: { files: pendingUploadFiles },
          } as React.ChangeEvent<HTMLInputElement>,
          pendingWorkspaceFileNames,
          pendingFolderId
        );
        clearPendingUploadState();
        clearConflictState();
      }
    } else if (pendingOneDriveFiles.length > 0) {
      const oneDriveMetadataList = await ensureTenantMetadata();
      if (oneDriveMetadataList && oneDriveMetadataList.length > 0) {
        const allFilesList = new DataTransfer();
        pendingOneDriveFiles.forEach((file) =>
          allFilesList.items.add(file.file)
        );
        setPendingMetadataFiles(allFilesList.files);
        setPendingMetadataWorkspaceFileNames(pendingWorkspaceFileNames);
        setPendingMetadataFolderId(pendingFolderId);
        setIsUploadMetadataDialogOpen(true);
        clearConflictState();
      } else {
        handleUploadFiles(
          pendingOneDriveFiles,
          pendingWorkspaceFileNames,
          pendingFolderId
        )
          .then(() => {
            clearPendingOneDriveState();
            clearPendingUploadState();
            clearConflictState();
          })
          .catch((error) => {
            logger.error("Failed to upload SharePoint/OneDrive files", {
              operation: "confirmOverwriteFiles",
              error,
              folderId: pendingFolderId,
              fileCount: pendingOneDriveFiles.length,
            });
            showErrorToast("Fejl ved upload af filer fra SharePoint/OneDrive.");
            clearPendingOneDriveState();
            clearPendingUploadState();
            clearConflictState();
          });
      }
    }
  }, [
    ensureTenantMetadata,
    ensureWorkspaceData,
    handleFolderUpload,
    handleUploadFiles,
    onUploadFiles,
    setPendingMetadataFiles,
    setPendingMetadataWorkspaceFileNames,
    setPendingMetadataFolderId,
    setIsUploadMetadataDialogOpen,
    clearConflictState,
    clearPendingUploadState,
    clearPendingOneDriveState,
  ]);

  const onMetadataCancel = useCallback(() => {
    clearPendingMetadataState();
    setIsUploadInProgress(false);
    uploadProcessedRef.current = false;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [clearPendingMetadataState, setIsUploadInProgress, fileInputRef]);

  const onMetadataConfirm = useCallback(
    async (cleanedMetadata: WorkspaceDocumentMetadata[][]) => {
      if (!isUploadInProgress && !uploadProcessedRef.current) {
        const {
          pendingUploadFiles,
          pendingWorkspaceFileNames,
          pendingFolderId,
          pendingOneDriveFiles,
          pendingMetadataFiles,
          pendingMetadataWorkspaceFileNames,
          pendingMetadataFolderId,
          pendingFolderMetadataMap,
          pendingFileMetadataMap,
        } = useUploadStateStore.getState();

        const capturedFiles: FileList | null =
          pendingMetadataFiles || pendingUploadFiles;
        const capturedWorkspaceFileNames: Record<string, string> =
          pendingMetadataWorkspaceFileNames || pendingWorkspaceFileNames;
        const capturedFolderId: string | undefined =
          pendingMetadataFolderId || pendingFolderId;
        const capturedOneDriveFiles = [...pendingOneDriveFiles];

        if (!capturedFiles && capturedOneDriveFiles.length === 0) {
          logger.error("Upload aborted: no files left for the metadata step", {
            operation: "confirmUploadMetadata",
            workspaceId,
          });
          showErrorToast("Ingen filer at uploade. Prøv igen.");
          return;
        }

        const currentWorkspaceId = workspaceId || currentWorkspaceIdRef.current;
        if (!currentWorkspaceId) {
          logger.error("Upload aborted: no workspace id", {
            operation: "confirmUploadMetadata",
          });
          showErrorToast("Workspace ID mangler. Prøv at opdatere siden.");
          return;
        }

        setIsUploadInProgress(true);
        uploadProcessedRef.current = true;

        try {
          const filesToCheck = capturedFiles;
          const hasFolderStructure =
            filesToCheck &&
            Array.from(filesToCheck).some(
              (file) => (file as FileWithRelativePath).webkitRelativePath
            );

          if (hasFolderStructure && filesToCheck) {
            const filenameToMetadata: Record<
              string,
              WorkspaceDocumentMetadata[]
            > = {};
            Array.from(filesToCheck).forEach((file, index) => {
              filenameToMetadata[file.name] = cleanedMetadata[index] || [];
            });

            const workspace = await ensureWorkspaceData();
            if (!workspace) {
              logger.error("Upload aborted: workspace data unavailable", {
                operation: "confirmUploadMetadata",
                workspaceId: currentWorkspaceId,
                folderId: capturedFolderId,
              });
              showErrorToast("Workspace data mangler. Prøv at opdatere siden.");
              setIsUploadInProgress(false);
              uploadProcessedRef.current = false;
              return;
            }

            try {
              let folderMetadataMapForUpload: FolderMetadataMap | undefined =
                undefined;
              if (
                pendingFolderMetadataMap &&
                pendingFolderMetadataMap.length > 0
              ) {
                folderMetadataMapForUpload = new Map(
                  pendingFolderMetadataMap.map(([path, metadata]) => [
                    path,
                    {
                      source: metadata.source,
                      driveId: metadata.driveId,
                      siteId: metadata.siteId,
                      sharepointFileId: metadata.sharepointFileId,
                    },
                  ])
                );
              }

              let fileMetadataMapForUpload:
                | Map<
                    string,
                    {
                      source: UploadSource;
                      driveId?: string;
                      siteId?: string;
                      sharepointFileId?: string;
                    }
                  >
                | undefined = undefined;

              if (pendingFileMetadataMap && pendingFileMetadataMap.length > 0) {
                fileMetadataMapForUpload = new Map(
                  pendingFileMetadataMap.map(
                    ([path, metadata]: [string, FileMetadata]) => [
                      path,
                      {
                        source: metadata.source,
                        driveId: metadata.driveId,
                        siteId: metadata.siteId,
                        sharepointFileId: metadata.sharepointFileId,
                      },
                    ]
                  )
                );
              }

              await handleFolderUpload(
                filesToCheck,
                workspace,
                capturedFolderId,
                filenameToMetadata,
                true,
                true,
                fileMetadataMapForUpload,
                folderMetadataMapForUpload
              );
              clearPendingMetadataState();
              setIsUploadInProgress(false);
              uploadProcessedRef.current = false;
              clearUploadMetadataStore();
              setPendingFolderMetadataMap(null);
            } catch (error) {
              logger.error("Failed to upload folder with metadata", {
                operation: "confirmUploadMetadata",
                error,
                workspaceId: currentWorkspaceId,
                folderId: capturedFolderId,
              });
              showErrorToast("Fejl ved upload af mappe med metadata");
              setIsUploadInProgress(false);
              uploadProcessedRef.current = false;
              clearPendingMetadataState();
              clearUploadMetadataStore();
              setPendingFolderMetadataMap(null);
            }
            return;
          }

          const filenameToMetadata: Record<
            string,
            WorkspaceDocumentMetadata[]
          > = {};
          const filesToUpload = capturedFiles;
          const workspaceFileNamesToUse = capturedWorkspaceFileNames;
          const folderIdToUse = capturedFolderId;

          if (capturedFiles) {
            Array.from(capturedFiles).forEach((file, index) => {
              filenameToMetadata[file.name] = cleanedMetadata[index] || [];
            });
          }

          if (capturedOneDriveFiles.length > 0) {
            await handleUploadFiles(
              capturedOneDriveFiles,
              capturedWorkspaceFileNames,
              capturedFolderId,
              filenameToMetadata
            );
            clearPendingOneDriveState();
          } else if (filesToUpload && filesToUpload.length > 0) {
            await onUploadFiles(
              {
                target: { files: filesToUpload },
              } as React.ChangeEvent<HTMLInputElement>,
              workspaceFileNamesToUse,
              folderIdToUse,
              filenameToMetadata
            );
          }
        } catch (error) {
          logger.error("Failed to upload files with metadata", {
            operation: "confirmUploadMetadata",
            error,
            workspaceId: currentWorkspaceId,
            folderId: capturedFolderId,
          });
          showErrorToast("Fejl ved upload af filer. Prøv igen.");
        } finally {
          clearPendingMetadataState();
          setIsUploadInProgress(false);
          uploadProcessedRef.current = false;
          clearUploadMetadataStore();
        }
      }
    },
    [
      workspaceId,
      currentWorkspaceIdRef,
      isUploadInProgress,
      ensureWorkspaceData,
      handleFolderUpload,
      handleUploadFiles,
      onUploadFiles,
      setIsUploadInProgress,
      clearPendingMetadataState,
      clearPendingOneDriveState,
      clearUploadMetadataStore,
      setPendingFolderMetadataMap,
    ]
  );

  return {
    onFileInputChange,
    onFolderInputChange,
    onOverwriteFilesCancel,
    onOverwriteFilesConfirm,
    onMetadataCancel,
    onMetadataConfirm,
    uploadProcessedRef,
  };
};
